/**
 * Torn item catalog.
 *
 * `/v2/torn/items` returns roughly 1 MB of JSON for every item in the game.
 * That is too big to keep pushing through localStorage, so the slimmed records
 * live in IndexedDB and are refreshed once a day. Everything the UI needs —
 * typeahead, market values, item types for the ranked tracker — reads from here.
 */

import { fetchItemCatalog } from "../api/torn.ts";
import { withKey } from "../api/keyring.ts";
import { Observable } from "../state/store.ts";
import type { CatalogItem } from "../state/types.ts";

const DB_NAME = "sniper";
const DB_VERSION = 1;
const STORE = "catalog";
const META_KEY = "meta";

const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface CatalogState {
  items: CatalogItem[];
  byId: Map<number, CatalogItem>;
  fetchedAt: number | null;
  loading: boolean;
  error: string | null;
}

export const catalog$ = new Observable<CatalogState>({
  items: [],
  byId: new Map(),
  fetchedAt: null,
  loading: false,
  error: null,
});

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB open failed"));
  });
}

async function idbGet<T>(key: string): Promise<T | undefined> {
  const db = await openDb();
  try {
    return await new Promise<T | undefined>((resolve, reject) => {
      const request = db.transaction(STORE, "readonly").objectStore(STORE).get(key);
      request.onsuccess = () => resolve(request.result as T | undefined);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
}

async function idbPut(key: string, value: unknown): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

interface StoredCatalog {
  items: CatalogItem[];
  fetchedAt: number;
}

function publish(items: CatalogItem[], fetchedAt: number | null): void {
  const byId = new Map<number, CatalogItem>();
  for (const item of items) byId.set(item.id, item);
  catalog$.update((state) => ({ ...state, items, byId, fetchedAt, loading: false, error: null }));
}

export function isStale(fetchedAt: number | null, now = Date.now()): boolean {
  return fetchedAt === null || now - fetchedAt > MAX_AGE_MS;
}

/** Loads the cached catalog, if any. Safe to call before a key exists. */
export async function loadCachedCatalog(): Promise<void> {
  try {
    const stored = await idbGet<StoredCatalog>(META_KEY);
    if (stored?.items?.length) publish(stored.items, stored.fetchedAt);
  } catch (err) {
    console.warn("[sniper] catalog cache unreadable", err);
  }
}

/**
 * Pulls a fresh catalog. Needs a working key, so callers should only invoke it
 * once the key pool has something valid in it.
 */
export async function refreshCatalog(force = false): Promise<boolean> {
  const state = catalog$.get();
  if (state.loading) return false;
  if (!force && !isStale(state.fetchedAt)) return false;

  catalog$.update((current) => ({ ...current, loading: true, error: null }));

  try {
    const records = await withKey((key) => fetchItemCatalog(key));
    if (records === null) {
      catalog$.update((current) => ({
        ...current,
        loading: false,
        error: "No key with budget available.",
      }));
      return false;
    }

    const items: CatalogItem[] = records
      .filter((record) => record.tradable)
      .map((record) => ({
        id: record.id,
        name: record.name,
        type: record.type,
        subType: record.subType,
        marketValue: record.marketValue,
        tradable: record.tradable,
      }));

    const fetchedAt = Date.now();
    publish(items, fetchedAt);
    await idbPut(META_KEY, { items, fetchedAt } satisfies StoredCatalog);
    return true;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    catalog$.update((current) => ({ ...current, loading: false, error: message }));
    return false;
  }
}

export function getItem(id: number): CatalogItem | undefined {
  return catalog$.get().byId.get(id);
}

/**
 * Typeahead over item names.
 *
 * Prefix matches rank above substring matches — typing "fea" should surface
 * "Feathery Hotel Coupon" before anything that merely contains those letters.
 */
export function searchItems(query: string, limit = 8, filter?: (item: CatalogItem) => boolean): CatalogItem[] {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return [];

  const prefix: CatalogItem[] = [];
  const contains: CatalogItem[] = [];

  for (const item of catalog$.get().items) {
    if (filter && !filter(item)) continue;
    const name = item.name.toLowerCase();
    if (name.startsWith(needle)) prefix.push(item);
    else if (name.includes(needle)) contains.push(item);
    if (prefix.length >= limit) break;
  }

  const byName = (a: CatalogItem, b: CatalogItem) => a.name.localeCompare(b.name);
  prefix.sort(byName);
  contains.sort(byName);
  return [...prefix, ...contains].slice(0, limit);
}

/** Item types the ranked tracker can meaningfully search. */
export const RANKED_TYPES = new Set(["Weapon", "Armor", "Clothing"]);

export function isRankedType(item: CatalogItem): boolean {
  return RANKED_TYPES.has(item.type);
}
